import React from 'react';
import styled from 'styled-components';
import { useFormContext, Controller, UseFormRegister, FieldErrors } from 'react-hook-form';
import { Input } from '../common/Input';
import { Select } from '../common/Select';
import { Toggle } from '../common/Toggle';
import Tooltip from '../common/Tooltip';
import { Button } from '../common/Button';
import OrderPreview from './OrderPreview';
import { OrderFormData, PriceTypeValues } from '../../validation/orderFormSchema';
import { 
  OrderType, 
  OrderSide, 
  AmountType, 
  MarginMode 
} from '../../types/preset';
import { SymbolPrecisionsResponse } from '../../services/infoService';

// --- Styled Components (Copied from OrderForm.tsx) --- 
const Row = styled.div`
  display: flex;
  gap: 0.8rem;
  align-items: flex-start;
`;

const Col = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 0.3rem;
  min-width: 0;
`;

const Label = styled.label`
  font-size: 0.8rem;
  color: #aaa;
  margin-bottom: 0.2rem;
`;

const CheckboxLabel = styled.label`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.9rem;
    color: #ccc;
    cursor: pointer;
    margin-top: 0.5rem;
`;

const CheckboxInput = styled.input.attrs({ type: 'checkbox' })`
    cursor: pointer;
`;

const ErrorText = styled.span`
    color: #f87171; // Tailwind red-400
    font-size: 0.75rem;
    margin-top: 0.1rem;
`;

// --- ButtonRow for Submit button ---
const ButtonRow = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 1.5rem;
`;

// --- Options (Copied & adapted from OrderForm.tsx) ---
const sideOptions = [
  { value: 'buy' as OrderSide, label: 'Buy/Long' },
  { value: 'sell' as OrderSide, label: 'Sell/Short' },
];

const orderTypeOptions = [
  { value: 'market' as OrderType, label: 'Market' },
  { value: 'limit' as OrderType, label: 'Limit' },
  { value: 'stop_market' as OrderType, label: 'Stop Market' },
  { value: 'stop_limit' as OrderType, label: 'Stop Limit' },
  { value: 'layered' as OrderType, label: 'Scale/Layered' },
  { value: 'cancel_all' as OrderType, label: 'Cancel All Orders' },
  { value: 'cancel_bids' as OrderType, label: 'Cancel Buy Orders' },
  { value: 'cancel_asks' as OrderType, label: 'Cancel Sell Orders' },
];

const priceTypeOptions = [
  { value: 'absolute' as PriceTypeValues, label: 'Absolute' },
  { value: 'best_bid' as PriceTypeValues, label: 'Best Bid' },
  { value: 'best_ask' as PriceTypeValues, label: 'Best Ask' },
];

const amountTypeOptions = [
  { value: 'base' as AmountType, label: 'BASE' }, // e.g., BTC
  { value: 'quote' as AmountType, label: 'QUOTE' }, // e.g., USD
  { value: 'percent' as AmountType, label: '%' },
];

const marginModeOptions = [
  { value: 'cross' as MarginMode, label: 'Cross' },
  { value: 'isolated' as MarginMode, label: 'Isolated' },
];

// --- Props Interface --- 
interface OrderFormFieldsProps {
  // Pass necessary react-hook-form methods and state
  control: any; // Control object from useForm
  register: UseFormRegister<OrderFormData>;
  watch: (name?: keyof OrderFormData | string) => any;
  errors: FieldErrors<OrderFormData>;
  precisions: SymbolPrecisionsResponse;
  symbols: string[];
  loadingSymbols: boolean;
  validationErrors: Record<string, string[]>;
  getPriceStep: (symbol: string) => number;
  getSizeStep: (symbol: string) => number;
  isSubmitting?: boolean;
}

// --- Component Implementation --- 
const OrderFormFields: React.FC<OrderFormFieldsProps> = ({
  control,
  register,
  watch,
  errors,
  precisions,
  symbols,
  loadingSymbols,
  validationErrors,
  getPriceStep,
  getSizeStep,
  isSubmitting = false,
}) => {
  // Watch relevant fields for conditional rendering
  const watchedOrderType = watch('order_type');
  const watchedLimitPriceType = watch('limit_price_type');
  const watchedStopLossEnabled = watch('stop_loss.enabled');
  const watchedTakeProfitEnabled = watch('take_profit.enabled');
  const watchedSymbol = watch('symbol');

  // Calculate derived values for conditional rendering
  const showLimitPrice = watchedOrderType === 'limit' || watchedOrderType === 'stop_limit';
  const showStopPrice = watchedOrderType === 'stop_market' || watchedOrderType === 'stop_limit';
  const showLayeredFields = watchedOrderType === 'layered';
  const showPriceType = showLimitPrice;
  const showOffsetInput = showLimitPrice && (watchedLimitPriceType === 'best_bid' || watchedLimitPriceType === 'best_ask');
  const showAbsolutePriceInput = showLimitPrice && watchedLimitPriceType === 'absolute';
  const showTradingFields = !['cancel_all', 'cancel_bids', 'cancel_asks'].includes(watchedOrderType as string);

  // Get all form values for preview
  const watchedValues = watch();

  return (
    <>
      {/* Symbol Selector */}
      <Row>
        <Col>
          <Label htmlFor="symbol">Symbol</Label>
          <Controller
            name="symbol"
            control={control}
            render={({ field }) => (
              <Select
                id="symbol"
                {...field}
                options={symbols.map(s => ({ value: s, label: s }))}
                disabled={loadingSymbols}
              />
            )}
          />
          {errors.symbol && <ErrorText>{errors.symbol.message as string}</ErrorText>}
        </Col>
      </Row>

      {/* Order Type & Side */}
      <Row>
        <Col>
          <Label htmlFor="order_type">Type</Label>
           <Controller
                name="order_type"
                control={control}
                render={({ field }) => (
                    <Select id="order_type" {...field} options={orderTypeOptions} />
                )}
            />
          {errors.order_type && <ErrorText>{errors.order_type.message as string}</ErrorText>}
        </Col>
        <Col>
          <Label htmlFor="side">Side</Label>
          <Controller
            name="side"
            control={control}
            render={({ field }) => (
              <Toggle 
                options={sideOptions} 
                onChange={field.onChange}
                value={field.value}
              />
            )}
          />
          {errors.side && <ErrorText>{errors.side.message as string}</ErrorText>}
        </Col>
      </Row>

     {/* Show trading-related fields only if it's not a cancellation order */}
     {showTradingFields && (
        <>
            {/* Amount & Amount Type */}
            <Row>
                <Col>
                    <Label htmlFor="amount">Amount</Label>
                    <Input
                        id="amount"
                        type="number"
                        step={watchedSymbol && precisions[watchedSymbol]?.size_decimals 
                            ? getSizeStep(watchedSymbol) 
                            : "any"}
                        {...register("amount", { valueAsNumber: true })}
                    />
                    {errors.amount && <ErrorText>{errors.amount.message as string}</ErrorText>}
                    {validationErrors.amount?.map((error, index) => (
                        <ErrorText key={`amount-error-${index}`}>{error}</ErrorText>
                    ))}
                </Col>
                <Col>
                    <Label htmlFor="amount_type">Amount Type</Label>
                     <Controller
                        name="amount_type"
                        control={control}
                        render={({ field }) => (
                            <Select id="amount_type" {...field} options={amountTypeOptions} />
                        )}
                    />
                    {errors.amount_type && <ErrorText>{errors.amount_type.message as string}</ErrorText>}
                </Col>
            </Row>

            {/* Limit Price / Price Type / Offset */}
            {showLimitPrice && (
                <Row>
                    {showPriceType && (
                         <Col>
                            <Label htmlFor="limit_price_type">Price Type</Label>
                            <Controller
                                name="limit_price_type"
                                control={control}
                                render={({ field }) => (
                                    <Select id="limit_price_type" {...field} options={priceTypeOptions} />
                                )}
                            />
                             {errors.limit_price_type && <ErrorText>{errors.limit_price_type.message as string}</ErrorText>}
                        </Col>
                    )}
                    {showAbsolutePriceInput && (
                        <Col>
                            <Label htmlFor="limit_price">Limit Price</Label>
                            <Input
                                id="limit_price"
                                type="number"
                                step={watchedSymbol && precisions[watchedSymbol]?.price_decimals 
                                    ? getPriceStep(watchedSymbol) 
                                    : "any"}
                                {...register("limit_price", { valueAsNumber: true })}
                            />
                            {errors.limit_price && <ErrorText>{errors.limit_price.message as string}</ErrorText>}
                            {validationErrors.limit_price?.map((error, index) => (
                                <ErrorText key={`limit-price-error-${index}`}>{error}</ErrorText>
                            ))}
                        </Col>
                    )}
                     {showOffsetInput && (
                         <Col>
                            <Label htmlFor="price_offset">Offset</Label>
                            <Input
                                id="price_offset"
                                type="number"
                                step={watchedSymbol && precisions[watchedSymbol]?.price_decimals 
                                    ? getPriceStep(watchedSymbol) 
                                    : "any"}
                                {...register("price_offset", { valueAsNumber: true })}
                                placeholder="e.g., -1 or 1"
                            />
                             {errors.price_offset && <ErrorText>{errors.price_offset?.message as string}</ErrorText>}
                        </Col>
                    )}
                </Row>
            )}

            {/* Stop Price */}
            {showStopPrice && (
                <Row>
                    <Col>
                        <Label htmlFor="stop_price">Stop Price</Label>
                        <Input
                            id="stop_price"
                            type="number"
                            step={watchedSymbol && precisions[watchedSymbol]?.price_decimals 
                                ? getPriceStep(watchedSymbol) 
                                : "any"}
                            {...register("stop_price", { valueAsNumber: true })}
                        />
                        {errors.stop_price && <ErrorText>{errors.stop_price.message as string}</ErrorText>}
                        {validationErrors.stop_price?.map((error, index) => (
                            <ErrorText key={`stop-price-error-${index}`}>{error}</ErrorText>
                        ))}
                    </Col>
                </Row>
            )}

             {/* Layered Order Fields */}
            {showLayeredFields && (
                <Row>
                    <Col>
                        <Label htmlFor="layered_parts">Parts</Label>
                        <Input
                            id="layered_parts"
                            type="number"
                            step="1"
                            {...register("layered.parts", { valueAsNumber: true })}
                        />
                         {errors.layered?.parts && <ErrorText>{errors.layered.parts.message as string}</ErrorText>}
                    </Col>
                    <Col>
                        <Label htmlFor="layered_step">Step</Label>
                        <Input
                            id="layered_step"
                            type="number"
                            step={watchedSymbol && precisions[watchedSymbol]?.price_decimals 
                                ? getPriceStep(watchedSymbol) 
                                : "any"}
                            {...register("layered.step", { valueAsNumber: true })}
                        />
                        {errors.layered?.step && <ErrorText>{errors.layered.step.message as string}</ErrorText>}
                    </Col>
                </Row>
            )}


            {/* Leverage & Margin Mode */}
            <Row>
                <Col>
                    <Label htmlFor="leverage">Leverage</Label>
                    <Input
                        id="leverage"
                        type="number"
                        step="0.1"
                        {...register("leverage", { valueAsNumber: true })}
                    />
                    {errors.leverage && <ErrorText>{errors.leverage.message as string}</ErrorText>}
                </Col>
                 <Col>
                    <Label htmlFor="margin_mode">Margin</Label>
                    <Controller
                        name="margin_mode"
                        control={control}
                        render={({ field }) => (
                            <Select id="margin_mode" {...field} options={marginModeOptions} />
                        )}
                    />
                    {errors.margin_mode && <ErrorText>{errors.margin_mode.message as string}</ErrorText>}
                </Col>
            </Row>

            {/* TP/SL Toggles and Inputs */}
            <Row>
                 <Col>
                    {/* Only show Post-Only for limit order types */}
                    {(watchedOrderType === 'limit' || watchedOrderType === 'stop_limit') && (
                        <CheckboxLabel>
                            <Controller
                                name="flags.post_only"
                                control={control}
                                render={({ field: { onChange, onBlur, value, ref } }) => (
                                    <CheckboxInput
                                        checked={!!value}
                                        onChange={onChange}
                                        onBlur={onBlur}
                                        ref={ref}
                                    />
                                )}
                            />
                            Post-Only
                            <Tooltip content="Ensures the order is added to the order book and not executed immediately. Provides maker fees.">
                              <span style={{ marginLeft: '4px', cursor: 'help' }}>ⓘ</span>
                            </Tooltip>
                        </CheckboxLabel>
                    )}
                     {errors.flags?.post_only && <ErrorText>{errors.flags.post_only?.message as string}</ErrorText>}
                 </Col>
                  <Col>
                     <CheckboxLabel>
                         <Controller
                            name="flags.reduce_only"
                            control={control}
                            render={({ field: { onChange, onBlur, value, ref } }) => (
                                <CheckboxInput
                                    checked={!!value}
                                    onChange={onChange}
                                    onBlur={onBlur}
                                    ref={ref}
                                />
                            )}
                        />
                        Reduce-Only
                         <Tooltip content="Ensures the order will only reduce your position size, not increase it.">
                           <span style={{ marginLeft: '4px', cursor: 'help' }}>ⓘ</span>
                         </Tooltip>
                    </CheckboxLabel>
                      {errors.flags?.reduce_only && <ErrorText>{errors.flags.reduce_only?.message as string}</ErrorText>}
                 </Col>
            </Row>

            {/* Place TP/SL in a more visible location for market orders */}
            {watchedOrderType === 'market' && (
                <Row>
                    <Col style={{ border: '1px solid #374151', borderRadius: '6px', padding: '10px', marginTop: '10px' }}>
                        <Label style={{ fontWeight: 'bold', color: '#e5e7eb' }}>Take Profit & Stop Loss Settings</Label>
                        <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {/* Take Profit Settings */}
                            <CheckboxLabel>
                                <Controller
                                    name="take_profit.enabled"
                                    control={control}
                                    render={({ field: { onChange, onBlur, value, ref } }) => (
                                        <CheckboxInput
                                            checked={!!value}
                                            onChange={onChange}
                                            onBlur={onBlur}
                                            ref={ref}
                                        />
                                    )}
                                />
                                Take Profit
                                <Tooltip content="Automatically close position with profit when price reaches this level">
                                  <span style={{ marginLeft: '4px', cursor: 'help' }}>ⓘ</span>
                                </Tooltip>
                            </CheckboxLabel>
                            {watchedTakeProfitEnabled && (
                                <Input
                                    id="take_profit_price"
                                    type="number"
                                    step={watchedSymbol && precisions[watchedSymbol]?.price_decimals 
                                        ? getPriceStep(watchedSymbol) 
                                        : "any"}
                                    placeholder="TP Trigger Price"
                                    {...register("take_profit.trigger_price_value", { valueAsNumber: true })}
                                    style={{ marginTop: '0.5rem' }}
                                />
                            )}
                            
                            {/* Stop Loss Settings */}
                            <CheckboxLabel style={{ marginTop: '8px' }}>
                                <Controller
                                    name="stop_loss.enabled"
                                    control={control}
                                    render={({ field: { onChange, onBlur, value, ref } }) => (
                                        <CheckboxInput
                                            checked={!!value}
                                            onChange={onChange}
                                            onBlur={onBlur}
                                            ref={ref}
                                        />
                                    )}
                                />
                                Stop Loss
                                <Tooltip content="Automatically close position to prevent further losses when price reaches this level">
                                  <span style={{ marginLeft: '4px', cursor: 'help' }}>ⓘ</span>
                                </Tooltip>
                            </CheckboxLabel>
                            {watchedStopLossEnabled && (
                                <Input
                                    id="stop_loss_price"
                                    type="number"
                                    step={watchedSymbol && precisions[watchedSymbol]?.price_decimals 
                                        ? getPriceStep(watchedSymbol) 
                                        : "any"}
                                    placeholder="SL Trigger Price"
                                    {...register("stop_loss.trigger_price_value", { valueAsNumber: true })}
                                    style={{ marginTop: '0.5rem' }}
                                />
                            )}
                        </div>
                    </Col>
                </Row>
            )}
        </>
     )} {/* End of showTradingFields block */}

      {/* Order Preview */}
      {showTradingFields && (
        <div style={{ marginTop: '1rem' }}>
          <OrderPreview 
            symbol={watchedValues.symbol} 
            side={watchedValues.side} 
            orderType={watchedValues.order_type}
            amount={String(watchedValues.amount || '')}
            amountType={watchedValues.amount_type}
            leverage={String(watchedValues.leverage || '')}
            limitPrice={watchedValues.limit_price ? String(watchedValues.limit_price) : undefined}
            limitPriceType={watchedValues.limit_price_type}
            priceOffset={watchedValues.price_offset ? String(watchedValues.price_offset) : undefined}
            stopPrice={watchedValues.stop_price ? String(watchedValues.stop_price) : undefined}
            takeProfitPrice={watchedValues.take_profit?.enabled && watchedValues.take_profit.trigger_price_value 
              ? String(watchedValues.take_profit.trigger_price_value) 
              : undefined}
            stopLossPrice={watchedValues.stop_loss?.enabled && watchedValues.stop_loss.trigger_price_value 
              ? String(watchedValues.stop_loss.trigger_price_value) 
              : undefined}
            marginMode={watchedValues.margin_mode}
            postOnly={!!watchedValues.flags?.post_only}
            reduceOnly={!!watchedValues.flags?.reduce_only}
          />
        </div>
      )}
      
      {/* Submit Button */}
      <ButtonRow>
        <Button 
          type="submit" 
          variant="primary" 
          size="large" 
          style={{ width: '100%', marginTop: '1rem' }}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Submitting...' : 'Submit Order'}
        </Button>
      </ButtonRow>
    </>
  );
};

export default OrderFormFields; 